import React, { useEffect, useState } from 'react';

import { Control } from '@vlabs/uikit';

import { useEventListFiltersTranslation } from '../../../i18n';
import { useEventListFilters } from '../../../useEventListFilters';
import st from '../../popovers.module.sass';

type CoordinateField = 'origin_longitude' | 'longitude_delta' | 'origin_latitude' | 'latitude_delta';

interface FieldConfig {
  field: CoordinateField;
  label: string;
  min: number;
  max: number;
}

export function CoordinatesTab(): React.JSX.Element {
  const { t } = useEventListFiltersTranslation();
  const { filters, updateFiltersDebounced, updateFilters } = useEventListFilters();

  const [local, setLocal] = useState<Record<CoordinateField, string>>({
    origin_longitude: '',
    longitude_delta: '',
    origin_latitude: '',
    latitude_delta: '',
  });
  const [errors, setErrors] = useState<Partial<Record<CoordinateField, string>>>({});

  useEffect(() => {
    setLocal({
      origin_longitude: filters.origin_longitude?.toString() ?? '',
      longitude_delta: filters.longitude_delta?.toString() ?? '',
      origin_latitude: filters.origin_latitude?.toString() ?? '',
      latitude_delta: filters.latitude_delta?.toString() ?? '',
    });
  }, [filters]);

  const handleChange = ({ field, min, max }: FieldConfig) => (value: string) => {
    setLocal((prev) => ({ ...prev, [field]: value }));

    if (value === '') {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
      updateFiltersDebounced({ [field]: undefined });
      return;
    }

    const parsed = Number(value);
    if (Number.isNaN(parsed)) {
      setErrors((prev) => ({ ...prev, [field]: t('validation_number') }));
      return;
    }
    if (parsed < min || parsed > max) {
      setErrors((prev) => ({
        ...prev,
        [field]: t('validation_range', { left: min, right: max }),
      }));
      return;
    }

    setErrors((prev) => ({ ...prev, [field]: undefined }));
    updateFiltersDebounced({ [field]: parsed });
  };

  const handleClear = (field: CoordinateField) => () => {
    setLocal((prev) => ({ ...prev, [field]: '' }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
    updateFilters({ [field]: undefined });
  };

  const renderInput = (config: FieldConfig) => (
    <div>
      <Control.Input
        errorMessage={errors[config.field]}
        hasError={!!errors[config.field]}
        id={config.field}
        isClearable
        label={config.label}
        name={config.field}
        onChangeValue={handleChange(config)}
        onClear={handleClear(config.field)}
        value={local[config.field]}
      />
    </div>
  );

  return (
    <div className={st.CoordinatesTabWrapper}>
      <div className={st.CoordinatesInputs}>
        <div className={st.RowInputsWrapper}>
          {renderInput({
            field: 'origin_longitude', label: t('origin_longitude'), min: -180, max: 180,
          })}
          {renderInput({
            field: 'longitude_delta', label: t('longitude_delta'), min: 0, max: 90,
          })}
        </div>
        <div className={st.RowInputsWrapper}>
          {renderInput({
            field: 'origin_latitude', label: t('origin_latitude'), min: -90, max: 90,
          })}
          {renderInput({
            field: 'latitude_delta', label: t('latitude_delta'), min: 0, max: 90,
          })}
        </div>
      </div>
    </div>
  );
}
